
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, CreditCard, Calendar, TrendingUp, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { creditsEvents } from '@/utils/creditsEvents';
import { creditsCache } from '@/utils/creditsCache';
import { settingsCache } from '@/utils/settingsCache';
import { PayPalSuccessModal } from './PayPalSuccessModal';

interface CreditSummary {
  daily_bonus: number;
  monthly_base: number;
  rollover: number;
  bonus: number;
  stored: number;
  total: number;
  low_credit_warning: boolean;
  current_cycle?: {
    cycle_start_date: string;
    cycle_end_date: string;
    billing_anchor_day: number;
  };
  credits_disabled?: boolean;
}

export function CreditBalanceCard() {
  const [credits, setCredits] = useState<CreditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState<any>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchCredits();
    checkForPayPalSuccess();
    
    // Listen for credit updates
    const unsubscribe = creditsEvents.subscribe((event) => {
      if (event.userId === user?.id) {
        fetchCredits();
      }
    });

    return unsubscribe;
  }, [user?.id]);

  const checkForPayPalSuccess = () => {
    try {
      const successData = localStorage.getItem('paypal_purchase_success');
      if (successData) {
        const purchaseData = JSON.parse(successData);
        
        // Check if the purchase was recent (within last 5 minutes)
        const isRecent = Date.now() - purchaseData.timestamp < 5 * 60 * 1000;
        
        if (isRecent) {
          setPurchaseDetails(purchaseData);
          setShowSuccessModal(true);
          
          // Clear the flag to prevent showing again
          localStorage.removeItem('paypal_purchase_success');
        }
      }
    } catch (error) {
      console.error('Error checking PayPal success:', error);
    }
  };

  const fetchCredits = async () => {
    try {
      setLoading(true);
      
      // Check if credits system is enabled first using cache
      const creditsEnabled = await settingsCache.getSetting('credits_enabled', false);
      
      if (creditsEnabled !== true) {
        setCredits({ credits_disabled: true } as CreditSummary);
        return;
      }

      // Use creditsCache with user ID from context
      if (!user?.id) {
        throw new Error('User not authenticated');
      }
      const data = await creditsCache.getCredits(user.id);
      setCredits(data);
    } catch (error) {
      console.error('Error fetching credits:', error);
      
      // Fallback to disabled state to prevent UI crashes
      setCredits({ credits_disabled: true } as CreditSummary);
      
      toast({
        title: "Credit System Unavailable", 
        description: "Credit information could not be loaded at this time",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    try {
      setRefreshing(true);
      
      // Clear cache and fetch fresh data
      if (user?.id) {
        creditsCache.clearUser(user.id);
        await fetchCredits();
        
        toast({
          title: "Credit balance updated successfully",
          description: "Your latest credit information has been loaded",
        });
      }
    } catch (error) {
      console.error('Error refreshing credits:', error);
      toast({
        title: "Refresh failed",
        description: "Unable to refresh credit balance. Please try again.",
        variant: "destructive",
      });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Credit Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded mb-4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded"></div>
              <div className="h-4 bg-muted rounded"></div>
              <div className="h-4 bg-muted rounded"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!credits || credits.credits_disabled) {
    return null; // Don't show if credits are disabled
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getCreditTypeLabel = (type: string) => {
    switch (type) {
      case 'daily_bonus': return 'Daily Bonus';
      case 'monthly_base': return 'Monthly Base';
      case 'rollover': return 'Rollover';
      case 'bonus': return 'Bonus';
      case 'stored': return 'Purchased';
      default: return type;
    }
  };

  const getCreditTypeDescription = (type: string) => {
    switch (type) {
      case 'daily_bonus': return 'Used first, expires daily';
      case 'monthly_base': return 'Used second, from your plan';
      case 'rollover': return 'Used third, from last month';
      case 'bonus': return 'Used fourth, special grants';
      case 'stored': return 'Used last, never expire';
      default: return '';
    }
  };

  return (
    <div className="space-y-4">
      {/* PayPal Success Modal */}
      {showSuccessModal && purchaseDetails && user?.id && (
        <PayPalSuccessModal
          isOpen={showSuccessModal}
          onClose={() => setShowSuccessModal(false)}
          purchaseDetails={purchaseDetails}
          userId={user.id}
        />
      )}
      
      {/* Main Credit Balance Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Credit Balance
          </CardTitle>
          <CardDescription>
            Credits are consumed in this order: Daily → Monthly → Rollover → Bonus → Purchased
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Total Credits Display */}
          <div className="text-center">
            <div className="text-3xl font-bold text-primary">
              {(credits.total || 0).toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground">Total Credits Available</div>
          </div>

          {/* Credit Breakdown */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { type: 'daily_bonus', value: credits.daily_bonus || 0, color: 'bg-green-100 text-green-800' },
              { type: 'monthly_base', value: credits.monthly_base || 0, color: 'bg-blue-100 text-blue-800' },
              { type: 'rollover', value: credits.rollover || 0, color: 'bg-yellow-100 text-yellow-800' },
              { type: 'bonus', value: credits.bonus || 0, color: 'bg-orange-100 text-orange-800' },
              { type: 'stored', value: credits.stored || 0, color: 'bg-purple-100 text-purple-800' }
            ].map(({ type, value, color }) => (
              <div key={type} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {getCreditTypeLabel(type)}
                  </span>
                  <Badge variant="secondary" className={color}>
                    {value.toLocaleString()}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {getCreditTypeDescription(type)}
                </div>
              </div>
            ))}
          </div>

          {/* Current Billing Cycle */}
          {credits.current_cycle && (
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 text-sm font-medium mb-2">
                <Calendar className="h-4 w-4" />
                Current Billing Cycle
              </div>
              <div className="text-sm text-muted-foreground space-y-1">
                <div>Start: {formatDate(credits.current_cycle.cycle_start_date)}</div>
                <div>End: {formatDate(credits.current_cycle.cycle_end_date)}</div>
                <div>Billing Day: {credits.current_cycle.billing_anchor_day} of each month</div>
              </div>
            </div>
          )}

          {/* Manual Refresh Section */}
          <div className="border-t pt-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="text-sm text-muted-foreground">
                Your credit balance does not always refresh in real-time.
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleManualRefresh}
                disabled={refreshing || loading}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Refreshing...' : 'Refresh Credit Balance'}
              </Button>
            </div>
          </div>

          {/* Buy More Credits Button */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                <CreditCard className="h-4 w-4" />
                Need More Credits?
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.location.href = '/credit-packages'}
                className="flex items-center gap-2"
              >
                <CreditCard className="h-4 w-4" />
                Buy More Credits
              </Button>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Purchase additional credits that never expire
            </div>
          </div>

          {/* Usage Info */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-2">
              <TrendingUp className="h-4 w-4" />
              Usage Information
            </div>
            <div className="text-sm text-muted-foreground space-y-1">
              <div>• 1 credit per call (inbound or outbound)</div>
              <div>• Credits are consumed in priority order</div>
              <div>• Monthly credits reset on your billing day</div>
              <div>• Unused monthly credits may rollover (plan dependent)</div>
              <div>• Bonus credits may have expiration dates</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
